import {
  Alert,
  Group,
  Paper,
  Progress,
  Stack,
  Table,
  Text,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { dashboardQueries } from '../../data-access/queries/dashboard.queries';
import { DashboardClause } from '../../data-access/types/stats';
import {
  computeCompletionRate,
  formatPercent,
  getCompletionColor,
} from '../../utils/completion-metrics';

import { useQueriesWithGlobal } from '@data-access';

type SortColumn = 'name' | 'hasDataRate' | 'hasFieldsRate';
type SortDirection = 'asc' | 'desc';

interface DefaultOsRecapSectionProps {
  clause: DashboardClause;
  campaignYear: number | null;
  ownerIds: number[] | null;
  showOwnerFilter: boolean;
}

interface DefaultOsRecapRowView {
  id: number;
  name: string;
  pratiquesCount: number;
  hasDataCount: number;
  hasFieldsCount: number;
  hasDataRate: number;
  hasFieldsRate: number;
}

const toggleSortDirection = (direction: SortDirection): SortDirection =>
  direction === 'asc' ? 'desc' : 'asc';

const compareValues = (
  left: string | number,
  right: string | number,
  direction: SortDirection,
) => {
  if (typeof left === 'string' && typeof right === 'string') {
    const result = left.localeCompare(right, 'fr');
    return direction === 'asc' ? result : -result;
  }

  const result = Number(left) - Number(right);
  return direction === 'asc' ? result : -result;
};

function SortableHeader({
  active,
  direction,
  label,
  onClick,
}: {
  active: boolean;
  direction: SortDirection;
  label: string;
  onClick: () => void;
}) {
  return (
    <UnstyledButton onClick={onClick}>
      <Group gap={4} wrap="nowrap">
        <Text fw={600} size="sm">
          {label}
        </Text>
        {active ? (
          <Text c="dimmed" size="xs">
            {direction === 'asc' ? '↑' : '↓'}
          </Text>
        ) : null}
      </Group>
    </UnstyledButton>
  );
}

function MetricCell({
  completed,
  label,
  rate,
  total,
}: {
  completed: number;
  label: string;
  rate: number;
  total: number;
}) {
  return (
    <Stack gap={4}>
      <Text fw={600} size="sm">
        {label}
      </Text>
      <Text size="sm">
        <strong>
          {completed} / {total}
        </strong>{' '}
        ({formatPercent(rate)})
      </Text>
      <Progress color={getCompletionColor(rate)} size="lg" value={rate} />
    </Stack>
  );
}

export function DefaultOsRecapSection({
  campaignYear,
  clause,
  ownerIds,
  showOwnerFilter,
}: DefaultOsRecapSectionProps) {
  const { t } = useTranslation();
  const [sortColumn, setSortColumn] = useState<SortColumn>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const queries = useQueriesWithGlobal(dashboardQueries);
  const recapQuery = useQuery({
    ...queries.defaultOrganizationsRecap(
      showOwnerFilter ? ownerIds : null,
      clause,
      campaignYear,
    ),
    enabled: clause !== 'new' || campaignYear != null,
  });

  const rows = useMemo<DefaultOsRecapRowView[]>(() => {
    return (recapQuery.data?.organizations ?? []).map((organization) => ({
      id: organization.id,
      name: organization.name,
      pratiquesCount: organization.pratiques_count,
      hasDataCount: organization.has_data_count,
      hasFieldsCount: organization.has_fields_count,
      hasDataRate: computeCompletionRate(
        organization.has_data_count,
        organization.pratiques_count,
      ),
      hasFieldsRate: computeCompletionRate(
        organization.has_fields_count,
        organization.pratiques_count,
      ),
    }));
  }, [recapQuery.data?.organizations]);

  const sortedRows = useMemo(() => {
    const nextRows = [...rows];

    nextRows.sort((left, right) => {
      if (sortColumn === 'name') {
        return compareValues(left.name, right.name, sortDirection);
      }
      if (sortColumn === 'hasDataRate') {
        return compareValues(left.hasDataRate, right.hasDataRate, sortDirection);
      }
      return compareValues(left.hasFieldsRate, right.hasFieldsRate, sortDirection);
    });

    return nextRows;
  }, [rows, sortColumn, sortDirection]);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(toggleSortDirection(sortDirection));
      return;
    }

    setSortColumn(column);
    setSortDirection(column === 'name' ? 'asc' : 'desc');
  };

  const subtitle =
    clause === 'gp'
      ? t('dashboard.default-os-recap.subtitle-gp', {
          defaultValue: 'OS suivis GP — toutes campagnes GP',
        })
      : t('dashboard.default-os-recap.subtitle-ngp', {
          campaignYear,
          defaultValue: 'OS suivis NGP — campagne {{campaignYear}}',
        });

  return (
    <Paper p="lg" withBorder>
      <Stack gap="md">
        <Stack gap={4}>
          <Title order={3}>
            {t('dashboard.default-os-recap.title', {
              defaultValue: 'Récapitulatif des OS par défaut',
            })}
          </Title>
          <Text c="dimmed" size="sm">
            {subtitle}
          </Text>
          <Text c="dimmed" size="xs">
            {t('dashboard.default-os-recap.sort-help', {
              defaultValue: 'Cliquez sur un en-tête pour trier.',
            })}
          </Text>
        </Stack>

        {recapQuery.isLoading ? (
          <Text c="dimmed" size="sm">
            {t('dashboard.default-os-recap.loading', {
              defaultValue: 'Chargement du récapitulatif OS...',
            })}
          </Text>
        ) : null}

        {recapQuery.isError ? (
          <Alert color="red" variant="light">
            {t('dashboard.default-os-recap.error', {
              defaultValue: 'Impossible de charger le récapitulatif OS.',
            })}
          </Alert>
        ) : null}

        {!recapQuery.isLoading && !recapQuery.isError && sortedRows.length === 0 ? (
          <Text c="dimmed" size="sm">
            {t('dashboard.default-os-recap.empty', {
              defaultValue: 'Aucun OS par défaut pour ce mode.',
            })}
          </Text>
        ) : null}

        {!recapQuery.isLoading && !recapQuery.isError && sortedRows.length > 0 ? (
          <Table highlightOnHover striped verticalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>
                  <SortableHeader
                    active={sortColumn === 'name'}
                    direction={sortDirection}
                    label={t('dashboard.default-os-recap.columns.organization', {
                      defaultValue: 'OS',
                    })}
                    onClick={() => handleSort('name')}
                  />
                </Table.Th>
                <Table.Th>
                  <SortableHeader
                    active={sortColumn === 'hasDataRate'}
                    direction={sortDirection}
                    label={t('dashboard.pratiques.kpis.has-data-rate', {
                      defaultValue: 'Données',
                    })}
                    onClick={() => handleSort('hasDataRate')}
                  />
                </Table.Th>
                <Table.Th>
                  <SortableHeader
                    active={sortColumn === 'hasFieldsRate'}
                    direction={sortDirection}
                    label={t('dashboard.pratiques.kpis.has-fields-rate', {
                      defaultValue: 'Parcelles',
                    })}
                    onClick={() => handleSort('hasFieldsRate')}
                  />
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {sortedRows.map((row) => (
                <Table.Tr key={row.id}>
                  <Table.Td>
                    <Text fw={600}>{row.name}</Text>
                  </Table.Td>
                  <Table.Td>
                    <MetricCell
                      completed={row.hasDataCount}
                      label={t('dashboard.pratiques.kpis.has-data-rate', {
                        defaultValue: 'Données',
                      })}
                      rate={row.hasDataRate}
                      total={row.pratiquesCount}
                    />
                  </Table.Td>
                  <Table.Td>
                    <MetricCell
                      completed={row.hasFieldsCount}
                      label={t('dashboard.pratiques.kpis.has-fields-rate', {
                        defaultValue: 'Parcelles',
                      })}
                      rate={row.hasFieldsRate}
                      total={row.pratiquesCount}
                    />
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        ) : null}
      </Stack>
    </Paper>
  );
}
